import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Books from './pages/Books';
import Users from './pages/Users';
import Sales from './pages/Sales';
import AdminBookUpload from './pages/AdminBookUpload';
import AdminBookEdit from './components/AdminBookEdit';
import AdminTicketManager from './components/AdminTicketManager';
import EnhancedPayouts from './pages/EnhancedPayouts';
import VoucherManagement from './pages/VoucherManagement';
import ServiceManagement from './pages/ServiceManagement';
import SupportTickets from './pages/SupportTickets';
import AdminISBNManagement from './pages/AdminISBNManagement';
import NewsTickerManagement from './pages/NewsTickerManagement';

// Import Music and Events Components
import MusicManagement from './pages/MusicManagement';
import MusicUpload from './pages/MusicUpload';
import EventsManagement from './pages/EventsManagement';
import EventCreate from './pages/EventCreate';
import AdminEditMusic from './components/AdminEditMusic';
import AdminLegalBusinessManagement from './pages/AdminLegalBusinessManagement';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children, requireSuperAdmin = false }) {
  const { user, loading, isSuperAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d1d1d]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if super admin access is required
  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">This page requires Super Administrator privileges.</p>
        </div>
      </div>
    );
  }
  
  return children;
}

function AppContent() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      {/* Dashboard */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Books Management */}
      <Route path="/books" element={
        <ProtectedRoute>
          <Layout>
            <Books />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/upload-book" element={
        <ProtectedRoute>
          <Layout>
            <AdminBookUpload />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/books/:bookId/edit" element={
        <ProtectedRoute>
          <Layout>
            <AdminBookEdit />
          </Layout>
        </ProtectedRoute>
      } />

       <Route path="/admin/tickets/:ticketId/edit" element={
        <ProtectedRoute>
          <Layout>
            <AdminTicketManager />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/music/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <AdminEditMusic />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Music Management */}
      <Route path="/admin/music" element={
        <ProtectedRoute>
          <Layout>
            <MusicManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/upload-music" element={
        <ProtectedRoute>
          <Layout>
            <MusicUpload />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/music/:musicId/edit" element={
        <ProtectedRoute>
          <Layout>
            <MusicUpload />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Events Management */}
      <Route path="/admin/events" element={
        <ProtectedRoute>
          <Layout>
            <EventsManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/create-event" element={
        <ProtectedRoute>
          <Layout>
            <EventCreate />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/events/:eventId/edit" element={
        <ProtectedRoute>
          <Layout>
            <EventCreate />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/legal-business/:type" element={
  <ProtectedRoute>
    <Layout>
      <AdminLegalBusinessManagement type={window.location.pathname.split('/').pop()} />
    </Layout>
  </ProtectedRoute>
} />
      
      {/* Super Admin Only Routes */}
      <Route path="/users" element={
        <ProtectedRoute requireSuperAdmin>
          <Layout>
            <Users />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales" element={
        <ProtectedRoute requireSuperAdmin>
          <Layout>
            <Sales />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/enhanced-payouts" element={
        <ProtectedRoute requireSuperAdmin>
          <Layout>
            <EnhancedPayouts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/vouchers" element={
        <ProtectedRoute requireSuperAdmin>
          <Layout>
            <VoucherManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/isbn-management" element={
        <ProtectedRoute requireSuperAdmin>
          <Layout>
            <AdminISBNManagement />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Staff & Super Admin Routes */}
      <Route path="/services" element={
        <ProtectedRoute>
          <Layout>
            <ServiceManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/services/audiobook" element={
        <ProtectedRoute>
          <Layout>
            <ServiceManagement initialTab="audiobook" />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/services/print" element={
        <ProtectedRoute>
          <Layout>
            <ServiceManagement initialTab="print" />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/support-tickets" element={
        <ProtectedRoute>
          <Layout>
            <SupportTickets />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/news-ticker" element={
        <ProtectedRoute>
          <Layout>
            <NewsTickerManagement />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;